import React from 'react'
import {
  Mail,
  Phone,
  Calendar,
  MapPin,
  CheckCircle,
  XCircle,
  Plus,
} from 'lucide-react'

const formatDate = (value) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const getInitials = (student, fullName) => {
  const first = student.first_name?.[0]
  const last = student.last_name?.[0]
  if (first || last) return `${first || ''}${last || ''}`.toUpperCase()
  const fromName = fullName?.trim()?.[0]
  if (fromName) return fromName.toUpperCase()
  return (student.username?.[0] || 'S').toUpperCase()
}

const getStatusMeta = (status) => {
  const st = (status || '').toLowerCase()
  if (st === 'approved' || st === 'active') {
    return {
      label: st === 'active' ? 'Active' : 'Approved',
      className: 'student-profile-card__badge--success',
    }
  }
  if (st === 'pending') {
    return { label: 'Pending', className: 'student-profile-card__badge--warning' }
  }
  if (st === 'rejected') {
    return { label: 'Rejected', className: 'student-profile-card__badge--danger' }
  }
  return { label: st || 'Unknown', className: 'student-profile-card__badge--muted' }
}

function InfoRow({ icon: Icon, label, value, emptyHint, onAdd }) {
  const hasValue = Boolean(value && String(value).trim())

  return (
    <div className={`student-profile-card__row${!hasValue ? ' is-empty' : ''}`}>
      <div className="student-profile-card__row-icon" aria-hidden="true">
        <Icon size={16} strokeWidth={2.25} />
      </div>
      <div className="student-profile-card__row-body">
        <span className="student-profile-card__row-label">{label}</span>
        {hasValue ? (
          <span className="student-profile-card__row-value">{value}</span>
        ) : (
          <div className="student-profile-card__empty">
            <span className="student-profile-card__empty-text">{emptyHint}</span>
            {onAdd ? (
              <button type="button" className="student-profile-card__empty-action" onClick={onAdd}>
                <Plus size={12} aria-hidden="true" />
                Add
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default function StudentProfileCard({
  student,
  fullName,
  status,
  actionLoading,
  onApprove,
  onReject,
  onEdit,
}) {
  const resolvedStatus = status || student.user_status || student.status || 'pending'
  const statusMeta = getStatusMeta(resolvedStatus)
  const initials = getInitials(student, fullName)
  const phone = student.phone_number || student.phone || ''
  const dob = formatDate(student.date_of_birth)
  const campus = student.campus_name || ''
  const showApprove = resolvedStatus === 'pending' || resolvedStatus === 'inactive'
  const showAdminActions =
    resolvedStatus === 'pending' ||
    resolvedStatus === 'inactive' ||
    resolvedStatus === 'approved'

  return (
    <section className="student-profile-card">
      <div className="student-profile-card__hero" aria-hidden="true" />

      <div className="student-profile-card__identity">
        <div className="student-profile-card__avatar" aria-hidden="true">
          {initials}
        </div>
        <h2>{fullName}</h2>
        <p className="student-profile-card__username">@{student.username}</p>
        <span className={`student-profile-card__badge ${statusMeta.className}`}>
          {statusMeta.label}
        </span>
      </div>

      <div className="student-profile-card__details">
        <InfoRow
          icon={Mail}
          label="Email"
          value={student.email}
          emptyHint="No email on file"
        />
        <InfoRow
          icon={Phone}
          label="Phone"
          value={phone}
          emptyHint="No phone number yet"
          onAdd={onEdit}
        />
        <InfoRow
          icon={Calendar}
          label="Date of birth"
          value={dob}
          emptyHint="Not on record"
          onAdd={onEdit}
        />
        <InfoRow
          icon={MapPin}
          label="Campus"
          value={campus}
          emptyHint="No campus assigned"
        />
      </div>

      {showAdminActions && (
        <div className="student-profile-card__actions">
          <p className="student-profile-card__actions-label">Admin actions</p>
          <div className="student-profile-card__actions-row">
            {showApprove && (
              <button
                type="button"
                className="btn btn-primary student-profile-card__btn"
                onClick={onApprove}
                disabled={actionLoading === 'status'}
              >
                <CheckCircle size={17} aria-hidden="true" />
                Approve
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline student-profile-card__btn student-profile-card__btn--danger"
              onClick={onReject}
              disabled={actionLoading === 'status'}
            >
              <XCircle size={17} aria-hidden="true" />
              Eject
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
